import { useApi } from '@/hooks/useApi';

import { Divider } from '@/components/atoms/divider';
import Heading from '@/components/atoms/heading';
import { sendGAEvent } from '@/components/integrations/trackingScripts';

import { apiEndpoints } from '@/constant/urls';
import {
  ContactSalesFormFields,
  ContactSalesFormSchema,
} from '@/features/modals/ContactSales/ContactSalesFormFields';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { currentLocaleConfig } from '@/lib/i18n/localeSpecific';
import IconBestValueProvider from '~/svg/IconBestValueProvider.svg';
import IconCheckmarkCircle from '~/svg/IconCheckmarkCircle.svg';

type ContactSalesFormProps = {
  onSuccess: () => void;
  onError: () => void;
};

export function ContactSalesForm({
  onSuccess,
  onError,
}: ContactSalesFormProps) {
  const { loading, makeApiCall } = useApi({
    url: apiEndpoints.contactSales,
    method: 'POST',
  });
  const { texts, lang } = useTranslation();

  const featureKeys: (keyof typeof texts.contact_sales.form.features)[] = [
    '65M_ips',
    'unlimited_connections',
    'full_anonymity',
    'free_features',
  ];
  const features = featureKeys.map(
    (key) => texts.contact_sales.form.features[key]
  );

  async function handleSubmit(data: ContactSalesFormSchema) {
    const response = await makeApiCall({
      body: {
        ...data,
        languageCode: lang === 'zh-cn' ? 'CN' : '', // empty string required
      } satisfies ContactSalesFormSchema,
    });

    if (response?.data) {
      onSuccess();
      sendGAEvent({
        event: 'ContactSalesForm_submitted',
      });
    } else {
      onError();
    }
  }

  return (
    <div className='flex flex-row gap-[3.25rem]'>
      <div className='hidden flex-1 flex-col md:flex'>
        <div className='text-neutral-N99 mb-12'>
          {currentLocaleConfig().logo()}
        </div>

        <Heading
          level='h2'
          classNames={{ root: 'mb-9' }}
          data-tracker-context='contact-sales-form_title'
        >
          {texts.contact_sales.form.high_speed_proxies}
        </Heading>

        <div className='mb-12 flex flex-col gap-6'>
          {features.map((feature, index) => (
            <div key={index} className='flex items-center gap-2'>
              <IconCheckmarkCircle className='text-primary-P99 h-5 w-5' />
              <div
                data-tracker-context={`contact-sales-form_${featureKeys[index]}`}
              >
                {feature}
              </div>
            </div>
          ))}
        </div>

        <div className='flex flex-row gap-6'>
          <IconBestValueProvider
            className='text-neutral-N50'
            data-tracker-context='contact-sales-form_award-1'
          />
          <IconBestValueProvider
            className='text-neutral-N50'
            data-tracker-context='contact-sales-form_award-2'
          />
          <IconBestValueProvider
            className='text-neutral-N50'
            data-tracker-context='contact-sales-form_award-3'
          />
        </div>
      </div>
      <div className='hidden md:block'>
        <Divider
          alignment='vertical'
          classNames={{ root: 'border-neutral-N10' }}
        />
      </div>

      <div className='flex flex-1'>
        <ContactSalesFormFields onSubmit={handleSubmit} loading={loading} />
      </div>
    </div>
  );
}
